import Vue from 'vue'

const authentication = (ctx) => {
  return {
    logout: async (status, doNotRedirect) => {
      if (status === 'energy') {
        await ctx.store.dispatch('energy/logout')
      } else {
        await ctx.store.dispatch('logout')
      }

      if (doNotRedirect) {
        return
      }

      const language = ctx.$storage.getUniversal('language') || 'en'
      return ctx.app.router.push({
        name: 'launcher',
        query: { status: status || 'overview', preferred_language: language },
        params: { id: Date.now() }
      })
    },
    login: async (jwtToken, isAccessToken) => {
      try {
        if (jwtToken) {
          const loginResponse = await ctx.$marlonApi.login(
            jwtToken,
            isAccessToken
          )
          if (!loginResponse || loginResponse?.success !== true) {
            return false
          }
          await ctx.store.dispatch('setMarlonToken', loginResponse.token)
        }
      } catch (err) {
        if (
          err.response &&
          err.response.data &&
          err.response.data.errors &&
          err.response.data.errors.length > 0 &&
          err.response.data.errors.includes('country_not_available')
        ) {
          // Status should be not_available, but this does not work yet
          return 'not_available'
        }
        if (
          err.response &&
          err.response.data &&
          err.response.data.errors &&
          err.response.data.errors.length > 0 &&
          err.response.data.errors[0] ===
            'JWT token could not be verified or was not valid.'
        ) {
          // 400 - JWT token could not be verified or was not valid
          return 'jwt_not_valid'
        }
        ctx.$sentry.setExtra('Marlon login failed', err)
        ctx.$sentry.captureException(err)
        return false
      }

      try {
        const accountResponse = await ctx.$marlonApi.getAccount()
        await ctx.store.dispatch(
          'account/setMarlonAccountDetails',
          accountResponse
        )

        if (isAccessToken && accountResponse) {
          const marlonToGigyaProfile = {
            country: accountResponse.country,
            email: accountResponse.customer_details?.email,
            firstName: accountResponse.customer_details?.first_name,
            lastName: accountResponse.customer_details?.last_name
          }
          await ctx.store.dispatch('setGigyaProfile', marlonToGigyaProfile)
        }
        return true
      } catch (err) {
        ctx.$sentry.setExtra('Marlon login failed', err)
        ctx.$sentry.captureException(err)
        return false
      }
    },
    redirectToCorrectStartScreen: async () => {
      try {
        const response = await ctx.$marlonApi.shortListInstallations()
        if (
          response &&
          response.installations &&
          response.installations.length >= 1
        ) {
          return { name: 'overview' }
        }

        return { name: 'index' }
      } catch (err) {
        if (
          err &&
          err.response &&
          err.response.status &&
          err.response.status === 500
        ) {
          // Status should be not_available, but this does not work yet
          return { name: 'landing', query: { state: 'not_available' } }
        }
        ctx.$sentry.setExtra('Retrieving short list installations failed', err)
        ctx.$sentry.captureException(err)
      }
    },
    setLanguageChoice: async (selectedLanguage) => {
      const profile =
        ctx.store.state.gigyaProfile ||
        ctx.$storage?.getUniversal('gigyaProfile') ||
        ctx.storage?.getUniversal('gigyaProfile')
      let lang = selectedLanguage

      const languageChoice = await ctx.store.dispatch('getLanguageChoice')

      if (profile !== null && languageChoice?.language !== selectedLanguage) {
        if (selectedLanguage?.toLowerCase().length <= 2) {
          if (!profile) {
            const fallbackLang = ctx.query?.preferred_language || 'en'
            lang = lang + `-${fallbackLang}`
          } else {
            lang = lang + `-${profile.country}`
          }
        }

        await ctx.store.dispatch('setLanguageChoice', {
          language: lang,
          email: profile?.email
        })
      }

      await ctx.store.dispatch('account/setLanguage', lang)
      ctx.store.commit('setLocale', lang)
      await ctx.$setI18nLocale(lang)
    },
    refreshAccountDetails: async () => {
      const gigyaToken = ctx.$storage.getUniversal('gigyaToken')
      const isAccessToken = {
        isAccessToken: ctx.$storage.getUniversal('isAccessToken')
      }
      const authResponse = await ctx.$apiAaltra.auth(gigyaToken, isAccessToken)
      if (authResponse) {
        ctx.store.dispatch('account/setAccountDetails', authResponse)
        ctx.$storage.setUniversal('aaltraToken', authResponse.access_token)
        return true
      }
    },
    executeLauncherQuery: async (status, query) => {
      switch (status.toLowerCase()) {
        case 'overview':
        case 'already_registered': {
          const redirect = await ctx.$redirectToCorrectStartScreen()
          return ctx.app.router.push(redirect)
        }
        case 'register': {
          let macAddressRequired = false
          if (query?.adapter_model) {
            const resultMaterialCodes = await ctx.store.dispatch(
              'registration/searchMaterialCode',
              { keyword: query?.adapter_model, launcherStatusIsRegister: true }
            )
            macAddressRequired = resultMaterialCodes?.materialcodes?.find(
              (c) =>
                c.material_code === query?.adapter_model &&
                c.type === 'lan_adapter'
            )?.mac_address_required

            // We take the result of iO instead of the query material code from Onecta
            // to make sure that we have the data exactly like iO needs it
            // eg. to fix uppercased / lowercased issues
            const lanAdapter = resultMaterialCodes?.materialcodes?.find(
              (c) => query?.adapter_model && c.type === 'lan_adapter'
            )
            if (lanAdapter) {
              query.adapter_model = lanAdapter.material_code
            }
          }

          const units = ctx.$unitsFromQuery(query, macAddressRequired)
          const gatewayDeviceId = query.gateway_device_id
          // vue-gtag is only installed on staging and production
          if (Vue.prototype.$gtag) {
            Vue.prototype.$gtag.event('registration_started_onecta', {})
          }

          if (units.length) {
            ctx.$startRegistrationWithUnits(units, gatewayDeviceId)
          } else {
            ctx.app.store.commit(
              'registration/setCurrentScreen',
              'registration-start'
            )
            ctx.$startRegistration(gatewayDeviceId)
          }
          break
        }
        case 'not_available':
          return ctx.app.router.push({
            name: 'landing',
            query: { state: 'not_available' }
          })
        case 'purchase': {
          const purchaseInfo = ctx.$storage.getUniversal('purchaseData')
          if (purchaseInfo && purchaseInfo.purchase_id === query.purchase_id) {
            return ctx.app.router.push({
              name: 'purchase-package-confirmed-purchase',
              query: { purchase_id: query.purchase_id }
            })
          } else {
            const redirect = await ctx.$redirectToCorrectStartScreen()
            return ctx.app.router.push(redirect)
          }
        }
        case 'energy': {
          await ctx.$refreshAccountDetails()
          return ctx.app.router.push({
            name: 'energy',
            params: { id: Date.now() }
          })
        }
        case 'authenticate': {
          if (query.code && query.code !== '') {
            const authenticateLauncherFlow =
              (await ctx.app.store.dispatch('getAuthenticateLauncherFlow')) ||
              'pv-optimisation'
            if (
              authenticateLauncherFlow &&
              authenticateLauncherFlow === 'pv-optimisation'
            ) {
              // Set PV Optimisation
              if (
                !ctx.app.store.state.energy.settings.pvoptimisation
                  .optimisationDetails
              ) {
                await ctx.app.store.dispatch(
                  'energy/settings/pvoptimisation/getOptimisationDetails'
                )
              }
              await ctx.app.store.commit(
                'energy/settings/pvoptimisation/setSettingsValue',
                {
                  key: 'authenticationCode',
                  value: query.code
                }
              )
              return ctx.app.router.push({
                name: 'energy-settings-pv-optimisation-dhw',
                query: { code: query.code }
              })
            }

            if (
              authenticateLauncherFlow &&
              authenticateLauncherFlow === 'energy-saving'
            ) {
              // Set Budget Control
              if (
                !ctx.app.store.state.energy.settings.energysaving
                  .energySavingDetails
              ) {
                await ctx.app.store.dispatch(
                  'energy/settings/energysaving/getBudgetControlSettings'
                )
              }
              await ctx.app.store.commit(
                'energy/settings/energysaving/setSettingsValue',
                {
                  key: 'authenticationCode',
                  value: query.code
                }
              )
              return ctx.app.router.push({
                name: 'energy-settings-energy-saving-set-point',
                query: { code: query.code }
              })
            }
          }

          return ctx.app.router.push({
            name: 'energy',
            params: { id: Date.now() }
          })
        }

        default:
          ctx.$sentry.captureMessage(
            'Redirected to landing, invalid url',
            'error'
          )
          return ctx.app.router.push({
            name: 'landing',
            query: { state: 'invalid_url' }
          })
      }
    }
  }
}

export default function(ctx, inject) {
  const isContext = !!(ctx && inject)
  const _ctx = isContext ? ctx : Vue.prototype
  const _authentication = authentication(_ctx)
  if (isContext) {
    ctx.$logout = _authentication.logout
    ctx.$login = _authentication.login
    ctx.$redirectToCorrectStartScreen =
      _authentication.redirectToCorrectStartScreen
    ctx.$setLanguageChoice = _authentication.setLanguageChoice
    ctx.$refreshAccountDetails = _authentication.refreshAccountDetails
    ctx.$executeLauncherQuery = _authentication.executeLauncherQuery
    inject('logout', _authentication.logout)
    inject('login', _authentication.login)
    inject(
      'redirectToCorrectStartScreen',
      _authentication.redirectToCorrectStartScreen
    )
    inject('setLanguageChoice', _authentication.setLanguageChoice)
    inject('refreshAccountDetails', _authentication.refreshAccountDetails)
    inject('executeLauncherQuery', _authentication.executeLauncherQuery)
  } else if (!window.$nuxt) {
    Vue.prototype.$logout = _authentication.logout
    Vue.prototype.$login = _authentication.login
    Vue.prototype.$redirectToCorrectStartScreen =
      _authentication.redirectToCorrectStartScreen
    Vue.prototype.$setLanguageChoice = _authentication.setLanguageChoice
    Vue.prototype.$refreshAccountDetails = _authentication.refreshAccountDetails
    Vue.prototype.$executeLauncherQuery = _authentication.executeLauncherQuery
    return _authentication
  }
}
